'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import Title, { TitleProps } from '@/components/content/common/Title'
import ExploreButton from '@/components/content/common/ExploreButton'
import { cn } from '@/lib/utils'

const container = cva('grid grid-cols-1 gap-y-5', {
  variants: {
    cols: {
      1: '',
      2: 'md:grid-cols-2 md:gap-x-12',
      3: 'md:grid-cols-2 lg:grid-cols-3 md:gap-8',
    },
  },
})

export type ListItemBase = {
  id?: number | string
  name?: string | null
  app?: { name?: string | null } | number | null
}

type ListProps<T extends ListItemBase> = VariantProps<typeof container> & {
  header?: TitleProps
  items: T[]
  ListItem: React.ComponentType<{ item: T }>
  button?: {
    label: string
    onClick: () => void
  }
  className?: string
}

export default function List<T extends ListItemBase>({
  header,
  items,
  ListItem,
  button,
  cols = 1,
  className,
}: ListProps<T>) {
  return (
    <div className={cn('flex w-full flex-col gap-4', className)}>
      {header && <Title {...header} />}
      <div className={cn(container({ cols }))}>
        {items.length > 0
          ? items.map((item, index) => {
              return <ListItem key={item.id ?? index} item={item} />
            })
          : null}
      </div>

      {button && <ExploreButton label={button.label} onClick={button.onClick} />}
    </div>
  )
}

export function EmptyResults({ query }: { query?: string }) {
  return (
    <div className="col-span-full my-12 flex w-full flex-col items-center justify-center gap-1 text-center">
      <h3 className="text-18 font-semibold text-gray-950">
        No results {query ? `for '${query}'` : 'found'}
      </h3>
      <p className="text-14 text-gray-600">
        We couldn't find anything matching your search. Try adjusting your query or exploring
        related searches.
      </p>
    </div>
  )
}
